import * as env from '../environment.mjs';
import UserAccount from './UserAccount.mjs';
import { FileTooLargeError, IncorrectFileTypeError } from '../custom-errors.mjs';

export default class WindCreekClient {
  constructor() {
    if (env.CURRENT === env.ENVIRONMENT_PRODUCTION) {
      this.hostname = 'https://windcreekcasino.com';
    } else {
      this.hostname = 'https://windcreekcasinodev.com';
    }

    if (env.MOCK_WC_GRAPHQL_API && env.CURRENT !== env.ENVIRONMENT_PRODUCTION) {
      this.hostname = env.MOCK_WC_GRAPHQL_API;
    }

    this.endpoint = '/api';

    /*
    This is almost always false, except when calling the logout function. This is necessary to avoid a logout loop.
    If we receive a 401 response from the server, we're supposed to call the logout mutation, but the logout mutation
    itself will also return a 401, so we ignore it just for that particular situation.
    */
    this.ignore401 = false;
  }

  query(query) {
    let requestBody = {};
    let rawRequestBody = '';
    let headers = new Headers();
    let requestOptions = {};

    // Set up the requestBody object based on the provided query - make sure it has all the required properties
    if (typeof query === 'string') {
      requestBody = {
        'query': query
      };
    } else if (typeof query === 'object') {
      requestBody = query;
    }

    rawRequestBody = JSON.stringify(requestBody);

    headers.append('Content-Type', 'application/json');
    headers.append('x-wcc-windcreekmobileapp', 'false');
    headers.append('X-WCC-PropertySite', 'true');
    headers.append('X-WCC-Property', WindCreekData?.propertyCode ?? '');

    if (UserAccount.isLoggedIn()) {
      headers.append('Authorization', 'Bearer ' + UserAccount.getAuthToken());
    }

    requestOptions = {
      method: 'POST',
      headers: headers,
      body: rawRequestBody,
      redirect: 'follow'
    };

    return fetch(this.hostname + this.endpoint + '/graphql', requestOptions)
      .then(response => {
        if (!response.ok) {
          if (response.status === 401 && this.ignore401 === false) {
            if (UserAccount.isLoggedIn()) {
              UserAccount.logout();
            }
          } else if (response.status === 412) {
            UserAccount.setStatusMaintenanceMode();
          } else if (response.status === 423) {
            if (location.pathname !== '/terms-of-service') {
              location.href = '/terms-of-service';
            }
          } else {
            throw new Error('Fetch call to Wind Creek GraphQL API failed for unknown reason. See network tab for details.');
          }
        }

        return response.json();
      });
  }

  uploadImage(file, endpoint) {
    let headers = new Headers();
    let requestOptions = {};

    const formData = new FormData();
    formData.append('picture', file);

    headers.append('x-wcc-windcreekmobileapp', 'false');
    headers.append('X-WCC-PropertySite', 'true');
    headers.append('X-WCC-Property', WindCreekData?.propertyCode ?? '');

    if (UserAccount.isLoggedIn()) {
      headers.append('Authorization', 'Bearer ' + UserAccount.getAuthToken());
    }

    requestOptions = {
      method: 'POST',
      headers: headers,
      body: formData,
      redirect: 'follow'
    };

    return fetch(this.hostname + this.endpoint + '/' + endpoint, requestOptions)
      .then(response => {
        if (response.status >= 200 && response.status < 300) {
          return response;
        } else if (response.status === 413) {
          throw new FileTooLargeError();
        } else {
          if (response.status === 400) {
            response.json().then((parsedResponse) => {
              if (parsedResponse?.error === 'unsupported image format') {
                throw new IncorrectFileTypeError();
              }
            });
          }
          throw new Error(`Unable to upload image to ${endpoint} endpoint. Check the network tab for details on the API call.`);
        }
      })
      .then(response => {
        return response.json();
      });
  }

  checkMaintenanceMode() {
    let headers = new Headers();
    let requestOptions = {};

    headers.append('x-wcc-windcreekmobileapp', 'false');
    headers.append('X-WCC-PropertySite', 'true');
    headers.append('X-WCC-Property', WindCreekData?.propertyCode ?? '');

    requestOptions = {
      method: 'GET',
      headers: headers,
      redirect: 'follow'
    };

    return fetch(this.hostname + this.endpoint + '/maintenance', requestOptions)
      .then(response => {
        if (!response.ok) {
          throw new Error('Unable to check API maintenance status.');
        }

        return response.json();
      });
  }
}
